import { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { apiGet, apiPost, apiPut } from '../../api/client';
import { PageHeader } from '../../components/common/PageHeader';
import { SelectField } from '../../components/fields/SelectField';
import { PermissionGate } from '../../components/auth/PermissionGate';
import { useAuthStore } from '../../store/auth.store';
import { LedgerPanel } from './LedgerPanel';
import { hhmmToMinutes, isMoney, minutesToHHMM, openingCountError, openingCountHint, STATUS_LABEL } from './branchCashLogic';

// Branch Cash - phase P1: switch a branch on, open its business day and verify the opening cash. Buttons follow the user's
// permissions, but the server decides: branch grants, permissions and every rule are enforced there. Later phases add the
// expense / receipt / transfer / closing screens beside this one.

const unwrap = (d: any) => d?.data ?? d;
const errMsg = (e: any, fallback: string) => e?.response?.data?.error ?? fallback;
const rupees = (n: number | null | undefined) => (n === null || n === undefined ? '-' : `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

function SettingsPanel({ branch }: { branch: any }) {
  const { enqueueSnackbar } = useSnackbar();
  const qc = useQueryClient();
  const [enabled, setEnabled] = useState(branch.isEnabled);
  const [start, setStart] = useState(minutesToHHMM(branch.dayStartMinutes));
  const [tz, setTz] = useState(branch.timezone);
  const [threshold, setThreshold] = useState(String(branch.varianceThreshold));
  useEffect(() => { setEnabled(branch.isEnabled); setStart(minutesToHHMM(branch.dayStartMinutes)); setTz(branch.timezone); setThreshold(String(branch.varianceThreshold)); }, [branch]);

  const startMinutes = hhmmToMinutes(start);
  const error = startMinutes === null ? 'Day start must be a time before 12:00 (e.g. 04:00)' : !isMoney(threshold) ? 'Enter the variance threshold as a rupee amount' : !tz.trim() ? 'Enter a timezone' : '';
  const save = useMutation({
    mutationFn: () => apiPut(`/branch-cash/settings/${branch.id}`, { isEnabled: enabled, timezone: tz.trim(), dayStartMinutes: startMinutes, varianceThreshold: threshold.trim() }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['bc-settings'] }); qc.invalidateQueries({ queryKey: ['bc-current'] }); enqueueSnackbar('Settings saved', { variant: 'success' }); },
    onError: (e: any) => enqueueSnackbar(errMsg(e, 'Could not save the settings'), { variant: 'error' }),
  });
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle1" gutterBottom>Day schedule &amp; variance threshold</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 1.5 }}>
        <SelectField label="Cash management" value={enabled ? 'on' : 'off'} onChange={(e) => setEnabled(e.target.value === 'on')}
          options={[{ value: 'off', label: 'Off' }, { value: 'on', label: 'On' }]} />
        <TextField label="Business day starts at (HH:MM)" value={start} onChange={(e) => setStart(e.target.value)} InputLabelProps={{ shrink: true }}
          helperText="A shop trading past midnight keeps one date until this time" />
        <TextField label="Timezone" value={tz} onChange={(e) => setTz(e.target.value)} InputLabelProps={{ shrink: true }} />
        <TextField label="Significant variance from (₹)" value={threshold} onChange={(e) => setThreshold(e.target.value)} InputLabelProps={{ shrink: true }}
          helperText="A difference this large needs a second person's approval" />
      </Box>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 1.5 }}>
        <Button variant="contained" disabled={!!error || save.isPending} onClick={() => save.mutate()}>Save settings</Button>
        {error && <Typography variant="caption" color="error">{error}</Typography>}
      </Stack>
    </Paper>
  );
}

function OpeningCountForm({ day, threshold }: { day: any; threshold: number }) {
  const { enqueueSnackbar } = useSnackbar();
  const qc = useQueryClient();
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [sourceRef, setSourceRef] = useState('');
  const hint = openingCountHint(day, amount, threshold);
  const error = openingCountError(day, amount, reason, sourceRef);
  const save = useMutation({
    mutationFn: () => apiPost(`/branch-cash/days/${day.id}/opening-count`, { amount: amount.trim(), reason: reason.trim() || undefined, sourceRef: sourceRef.trim() || undefined }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['bc-current'] }); qc.invalidateQueries({ queryKey: ['bc-days'] }); setAmount(''); setReason(''); setSourceRef(''); enqueueSnackbar('Opening count recorded', { variant: 'success' }); },
    onError: (e: any) => enqueueSnackbar(errMsg(e, 'Could not record the count'), { variant: 'error' }),
  });
  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        {day.openingBasis === 'ESTABLISHED' ? 'First opening - establish the cash balance' : `Count the cash in hand (expected ${rupees(day.expectedOpening)})`}
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 1.5 }}>
        <TextField label="Counted cash (₹)" value={amount} onChange={(e) => setAmount(e.target.value)} InputLabelProps={{ shrink: true }} />
        <TextField label={hint.needsReason ? 'Reason (required)' : 'Reason'} value={reason} onChange={(e) => setReason(e.target.value)} InputLabelProps={{ shrink: true }} />
        {day.openingBasis === 'ESTABLISHED' && (
          <TextField label="Source reference (required)" value={sourceRef} onChange={(e) => setSourceRef(e.target.value)} InputLabelProps={{ shrink: true }}
            helperText="Cash book page, handover slip, migration batch..." />
        )}
      </Box>
      {day.openingBasis === 'ESTABLISHED' && (
        <Alert severity="info" sx={{ mt: 1 }}>
          A first opening balance is not final when you record it: a different person must check it against the source and verify it before the day can take entries.
        </Alert>
      )}
      {hint.variance !== null && hint.variance !== 0 && (
        <Alert severity={hint.needsApproval ? 'warning' : 'info'} sx={{ mt: 1 }}>
          {hint.variance < 0 ? `Shortage of ${rupees(-hint.variance)}` : `Excess of ${rupees(hint.variance)}`}
          {hint.needsApproval ? ' - this is a significant difference and needs another person\'s approval before the day can take entries.' : ' - it will be recorded with your explanation.'}
        </Alert>
      )}
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 1.5 }}>
        <Button variant="contained" disabled={!!error || save.isPending} onClick={() => save.mutate()}>Record count</Button>
        {error && amount !== '' && <Typography variant="caption" color="error">{error}</Typography>}
      </Stack>
    </Box>
  );
}

function ApprovalBar({ day }: { day: any }) {
  const { enqueueSnackbar } = useSnackbar();
  const qc = useQueryClient();
  const me = useAuthStore((s) => s.user?.id);
  const [note, setNote] = useState('');
  const first = day.openingBasis === 'ESTABLISHED';
  // Verifying a FIRST opening: re-type the amount and source reference you checked against the document (the server compares them to the count).
  const [confirmAmount, setConfirmAmount] = useState('');
  const [confirmRef, setConfirmRef] = useState('');
  const decide = useMutation({
    mutationFn: (what: 'approve' | 'reject') => apiPost(`/branch-cash/days/${day.id}/opening-variance/${what}`,
      { note: note.trim() || undefined, ...(what === 'approve' && first ? { amount: confirmAmount.trim(), sourceRef: confirmRef.trim() } : {}) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['bc-current'] }); qc.invalidateQueries({ queryKey: ['bc-days'] }); setNote(''); setConfirmAmount(''); setConfirmRef(''); enqueueSnackbar('Decision recorded', { variant: 'success' }); },
    onError: (e: any) => enqueueSnackbar(errMsg(e, 'Could not record the decision'), { variant: 'error' }),
  });
  const own = day.openingCountedById === me;
  return (
    <Alert severity="warning">
      {first
        ? <>First opening balance of {rupees(day.actualOpening)} recorded from "{day.openingSourceRef ?? '-'}". Reason given: {day.openingReason ?? '-'}</>
        : <>Counted {rupees(day.actualOpening)} against {rupees(day.expectedOpening)} expected ({rupees(day.openingVariance)}). Reason given: {day.openingReason ?? '-'}</>}
      {/* A FIRST opening is verified under its own permission; cash:variance_approve alone never verifies it (the server enforces the same). */}
      <PermissionGate permission={first ? 'cash:opening_verify' : 'cash:variance_approve'}>
        {own ? <Typography variant="caption" display="block">You entered this count, so another person must {first ? 'verify' : 'approve'} it.</Typography> : (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
            {first && <TextField size="small" label="Amount you checked (₹)" value={confirmAmount} onChange={(e) => setConfirmAmount(e.target.value)} InputLabelProps={{ shrink: true }} />}
            {first && <TextField size="small" label="Source you checked" value={confirmRef} onChange={(e) => setConfirmRef(e.target.value)} InputLabelProps={{ shrink: true }} />}
            <TextField size="small" label="Note (required to reject)" value={note} onChange={(e) => setNote(e.target.value)} InputLabelProps={{ shrink: true }} />
            <Button size="small" variant="contained" disabled={decide.isPending || (first && (!isMoney(confirmAmount) || !confirmRef.trim()))} onClick={() => decide.mutate('approve')}>{first ? 'Verify' : 'Approve'}</Button>
            <Button size="small" color="error" disabled={decide.isPending || !note.trim()} onClick={() => decide.mutate('reject')}>Reject - recount</Button>
          </Stack>
        )}
      </PermissionGate>
    </Alert>
  );
}

export function BranchCashPage() {
  const { enqueueSnackbar } = useSnackbar();
  const qc = useQueryClient();
  const settingsQ = useQuery({ queryKey: ['bc-settings'], queryFn: async () => unwrap(await apiGet('/branch-cash/settings')) as any[] });
  const branches = settingsQ.data ?? [];
  const [branchId, setBranchId] = useState('');
  useEffect(() => { if (!branchId && branches.length) setBranchId(branches[0].id); }, [branches, branchId]);
  const branch = useMemo(() => branches.find((b) => b.id === branchId), [branches, branchId]);

  const currentQ = useQuery({ queryKey: ['bc-current', branchId], enabled: !!branchId, queryFn: async () => unwrap(await apiGet('/branch-cash/days/current', { branchId })) as any });
  const daysQ = useQuery({ queryKey: ['bc-days', branchId], enabled: !!branchId, queryFn: async () => unwrap(await apiGet('/branch-cash/days', { branchId, limit: 15 })) as any[] });
  const open = useMutation({
    mutationFn: () => apiPost('/branch-cash/days/open', { branchId }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['bc-current'] }); qc.invalidateQueries({ queryKey: ['bc-days'] }); enqueueSnackbar('Business day opened', { variant: 'success' }); },
    onError: (e: any) => enqueueSnackbar(errMsg(e, 'Could not open the business day'), { variant: 'error' }),
  });

  const cur = currentQ.data;
  const day = cur?.activeDay;
  return (
    <Box>
      <PageHeader title="Branch Cash" subtitle="Open the business day and verify the opening cash" />
      {settingsQ.isError && <Alert severity="error" sx={{ mb: 2 }}>{errMsg(settingsQ.error, 'Could not load branches')}</Alert>}
      <Stack spacing={2}>
        <Box sx={{ maxWidth: 320 }}>
          <SelectField label="Branch" value={branchId} onChange={(e) => setBranchId(e.target.value)}
            options={branches.map((b) => ({ value: b.id, label: `${b.branchCode} - ${b.name}${b.isEnabled ? '' : ' (off)'}` }))} />
        </Box>

        {branch && (
          <PermissionGate permission="cash:config_rules"><SettingsPanel branch={branch} /></PermissionGate>
        )}

        {cur && (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="subtitle1">{day ? `Business day ${day.businessDate}` : 'No business day open'}</Typography>
              {day && <Chip size="small" label={STATUS_LABEL[day.status] ?? day.status} />}
              {day && <Chip size="small" color={day.openingStatus === 'VERIFIED' ? 'success' : 'warning'} label={day.openingStatus === 'PENDING_APPROVAL' && day.openingBasis === 'ESTABLISHED' ? 'Awaiting independent verification' : (STATUS_LABEL[day.openingStatus] ?? day.openingStatus)} />}
            </Stack>

            {!day && (
              <Stack spacing={1}>
                {cur.openBlocker ? <Alert severity="info">{cur.openBlocker}</Alert> : (
                  <Typography variant="body2">
                    {cur.nextOpeningBasis === 'CARRIED_FORWARD'
                      ? `Opens with ${rupees(cur.nextExpectedOpening)} carried forward from the last close, to be confirmed by a physical count.`
                      : 'This is the first day for this branch - the opening cash must be established with a reason.'}
                  </Typography>
                )}
                <PermissionGate permission="cash:open_day">
                  <Box><Button variant="contained" disabled={!cur.canOpen || open.isPending} onClick={() => open.mutate()}>Open business day</Button></Box>
                </PermissionGate>
              </Stack>
            )}

            {day && day.openingStatus === 'AWAITING_COUNT' && (
              <PermissionGate permission="cash:opening_count" fallback={<Alert severity="info">Waiting for the opening cash to be counted.</Alert>}>
                <OpeningCountForm day={day} threshold={cur.settings.varianceThreshold} />
              </PermissionGate>
            )}
            {day && day.openingStatus === 'PENDING_APPROVAL' && <ApprovalBar day={day} />}
            {day && day.openingStatus === 'VERIFIED' && (
              <Alert severity="success">
                Opening cash {rupees(day.actualOpening)} verified
                {day.openingVariance ? ` (difference ${rupees(day.openingVariance)}: ${day.openingReason})` : day.openingBasis === 'ESTABLISHED' ? ` (established: ${day.openingReason})` : ''}.
                The day is ready for cash entries.
              </Alert>
            )}
          </Paper>
        )}

        {day && <PermissionGate permission="cash:view"><LedgerPanel day={day} /></PermissionGate>}

        {!!daysQ.data?.length && (
          <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead><TableRow>
                <TableCell>Business date</TableCell><TableCell>Status</TableCell><TableCell>Opening</TableCell>
                <TableCell align="right">Expected</TableCell><TableCell align="right">Counted</TableCell><TableCell align="right">Difference</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {daysQ.data.map((d) => (
                  <TableRow key={d.id}>
                    <TableCell>{d.businessDate}</TableCell><TableCell>{STATUS_LABEL[d.status] ?? d.status}</TableCell>
                    <TableCell>{STATUS_LABEL[d.openingStatus] ?? d.openingStatus}</TableCell>
                    <TableCell align="right">{rupees(d.expectedOpening)}</TableCell><TableCell align="right">{rupees(d.actualOpening)}</TableCell>
                    <TableCell align="right">{rupees(d.openingVariance)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        )}
      </Stack>
    </Box>
  );
}
